// M32 — Feedback dialog. Mobile-first by design:
//   - Full-screen on `sm down` (phones). The dialog acts like a native
//     app screen with a sticky top app bar (close X on the left, the
//     primary "Send" action on the right) and a scrollable body.
//   - Tap targets sized for thumbs (kind chips are 48px tall, Send is
//     a full-width button on mobile).
//   - The "What we'll send" panel exposes every auto-captured field
//     including the mobile-specific ones (orientation, touch, network
//     type, etc.) so users can trust what's leaving their device.
//
// State / submission ownership: this component is presentational.
// FeedbackProvider holds `open` state and the OpenFeedbackOptions; this
// component receives them via props and pushes the submission back via
// `onSubmitted`. Keeps fast-refresh happy and the dialog testable in
// isolation.

import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportOutlined from '@mui/icons-material/BugReportOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import HelpOutline from '@mui/icons-material/HelpOutline';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import {
  FEEDBACK_MESSAGE_MAX,
  collectMetadata,
  submitFeedback,
} from '../lib/feedback';
import { captureScreenshot, type CapturedScreenshot } from '../lib/screenshot';
import type { FeedbackKind, FeedbackMetadata } from '../lib/types';
import type { OpenFeedbackOptions } from './useFeedback';

const KINDS: Array<{ value: FeedbackKind; label: string; icon: React.ReactNode }> = [
  { value: 'bug', label: 'Bug', icon: <BugReportOutlined fontSize="small" /> },
  { value: 'suggestion', label: 'Suggestion', icon: <LightbulbOutlined fontSize="small" /> },
  { value: 'question', label: 'Question', icon: <HelpOutline fontSize="small" /> },
  { value: 'other', label: 'Other', icon: <ChatBubbleOutline fontSize="small" /> },
];

export interface FeedbackDialogProps {
  open: boolean;
  initialOptions: OpenFeedbackOptions;
  onClose: () => void;
}

export default function FeedbackDialog({ open, initialOptions, onClose }: FeedbackDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [kind, setKind] = useState<FeedbackKind>(initialOptions.kind ?? 'suggestion');
  const [message, setMessage] = useState<string>(initialOptions.prefillMessage ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Screenshot capture state. The capture runs each time the dialog
  // opens (see the effect below); the user can opt out via the toggle.
  const [screenshot, setScreenshot] = useState<CapturedScreenshot | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureFailed, setCaptureFailed] = useState(false);
  const [includeScreenshot, setIncludeScreenshot] = useState(true);

  // Re-snapshot metadata each time the dialog opens so it reflects the
  // page / viewport the user is currently on (not the page they were on
  // when FeedbackProvider mounted).
  const metadata: FeedbackMetadata = useMemo(
    () => (open ? collectMetadata() : emptyForDisplay()),
    [open],
  );

  // Reset form state every time we open with a fresh set of options.
  useEffect(() => {
    if (!open) return;
    setKind(initialOptions.kind ?? 'suggestion');
    setMessage(initialOptions.prefillMessage ?? '');
    setSubmittedAt(null);
    setError(null);
  }, [open, initialOptions]);

  // Capture a screenshot of the page each time the dialog opens. The
  // dialog portals out of #root, so html2canvas(#root) grabs the page
  // underneath — never this dialog. Best-effort: feedback still sends
  // if the capture fails or the user toggles it off.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    setScreenshot(null);
    setScreenshotUrl(null);
    setCaptureFailed(false);
    setIncludeScreenshot(true);
    setCapturing(true);

    captureScreenshot()
      .then((shot) => {
        if (cancelled) return;
        if (shot) {
          objectUrl = URL.createObjectURL(shot.blob);
          setScreenshot(shot);
          setScreenshotUrl(objectUrl);
        } else {
          setCaptureFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setCaptureFailed(true);
      })
      .finally(() => {
        if (!cancelled) setCapturing(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open]);

  const charsLeft = FEEDBACK_MESSAGE_MAX - message.length;
  const overLimit = charsLeft < 0;
  const canSubmit = !submitting && message.trim().length > 0 && !overLimit;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback({
        kind,
        message,
        metadata,
        errorContext: initialOptions.errorContext ?? null,
        screenshot: includeScreenshot ? screenshot?.blob ?? null : null,
      });
      setSubmittedAt(Date.now());
      // Auto-close after a beat so the user sees the success state.
      setTimeout(() => onClose(), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Body content (shared between full-screen + dialog layouts) ──
  const bodyContent = (
    <Stack spacing={2.5}>
      {submittedAt && (
        <Alert severity="success">
          Thanks! Your feedback has been sent.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          What kind of feedback is this?
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            // Override Stack's spacing on wrap so chips don't collide.
            '& > *': { mt: '0 !important', ml: '0 !important' },
          }}
        >
          {KINDS.map((k) => (
            <Chip
              key={k.value}
              icon={k.icon as React.ReactElement}
              label={k.label}
              variant={kind === k.value ? 'filled' : 'outlined'}
              color={kind === k.value ? 'primary' : 'default'}
              onClick={() => setKind(k.value)}
              // Larger tap target on touch devices. Material spec calls
              // for 48px minimum; default Chip is 32px.
              sx={{ height: 40, px: 0.5, fontSize: '0.9rem' }}
            />
          ))}
        </Stack>
      </Box>

      <TextField
        label="What's on your mind?"
        placeholder={placeholderFor(kind)}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        multiline
        minRows={5}
        maxRows={12}
        fullWidth
        error={overLimit}
        helperText={
          overLimit
            ? `Over the limit by ${Math.abs(charsLeft)} characters.`
            : `${charsLeft.toLocaleString()} characters left`
        }
        // Don't yank focus + open the keyboard on phones the moment
        // the dialog mounts — feels jarring.
        autoFocus={!isMobile}
        inputProps={{ 'aria-label': 'Feedback message' }}
      />

      <ScreenshotSection
        capturing={capturing}
        captureFailed={captureFailed}
        screenshotUrl={screenshotUrl}
        included={includeScreenshot}
        onToggle={setIncludeScreenshot}
      />

      <Accordion
        disableGutters
        sx={{
          boxShadow: 'none',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" color="text.secondary">
            What we'll send along with your message
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <MetadataPreview metadata={metadata} />
        </AccordionDetails>
      </Accordion>

      <Typography variant="caption" color="text.secondary">
        We never send your password. Only your message, the technical
        details above, and — if you leave it on — the screenshot are
        included.
      </Typography>
    </Stack>
  );

  // ── Mobile: full-screen with sticky AppBar + bottom action area ──
  if (isMobile) {
    return (
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        aria-labelledby="feedback-dialog-title"
      >
        <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Toolbar>
            <IconButton edge="start" onClick={onClose} aria-label="Close feedback dialog">
              <CloseIcon />
            </IconButton>
            <Typography id="feedback-dialog-title" variant="h6" sx={{ flex: 1, ml: 1 }}>
              Send feedback
            </Typography>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              variant="contained"
              size="medium"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submitting ? 'Sending' : 'Send'}
            </Button>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            p: 2,
            // Respect the iOS home-indicator safe area so the last input
            // doesn't sit behind the system gesture bar.
            pb: 'calc(env(safe-area-inset-bottom) + 24px)',
            overflowY: 'auto',
          }}
        >
          {bodyContent}
        </Box>
      </Dialog>
    );
  }

  // ── Desktop: standard centered dialog ──
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="feedback-dialog-title">
      <DialogTitle id="feedback-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Send feedback
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} size="small" aria-label="Close feedback dialog">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {bodyContent}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function placeholderFor(kind: FeedbackKind): string {
  switch (kind) {
    case 'bug':
      return 'What were you trying to do? What happened instead?';
    case 'suggestion':
      return 'What would make this better?';
    case 'question':
      return 'What do you need help figuring out?';
    case 'other':
      return 'Tell us anything…';
  }
}

function emptyForDisplay(): FeedbackMetadata {
  return {
    page_url: null,
    app_version: null,
    user_agent: null,
    locale: null,
    timezone: null,
    viewport_width: null,
    viewport_height: null,
    screen_width: null,
    screen_height: null,
    device_pixel_ratio: null,
    is_touch_device: null,
    orientation: null,
    platform: null,
    is_standalone: null,
    connection_type: null,
    device_memory: null,
  };
}

// ─── Screenshot section ──────────────────────────────────────────
// Preview + opt-out toggle for the auto-captured page screenshot.
// Capture itself lives in lib/screenshot.ts; this is purely the UI.
// Capturing → skeleton; failed → a quiet note (feedback still sends);
// ready → the preview, dimmed + desaturated when the user opts out.
function ScreenshotSection({
  capturing,
  captureFailed,
  screenshotUrl,
  included,
  onToggle,
}: {
  capturing: boolean;
  captureFailed: boolean;
  screenshotUrl: string | null;
  included: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ImageOutlined fontSize="small" color="action" />
        <Typography variant="body2" sx={{ flex: 1 }}>
          Screenshot of this page
        </Typography>
        {!captureFailed && (
          <Switch
            size="small"
            checked={included}
            onChange={(e) => onToggle(e.target.checked)}
            disabled={capturing}
            slotProps={{
              input: { 'aria-label': 'Include this screenshot', role: 'switch' },
            }}
          />
        )}
      </Stack>

      {capturing && <Skeleton variant="rounded" height={150} sx={{ mt: 1 }} />}

      {!capturing && captureFailed && (
        <Typography variant="caption" color="text.secondary">
          Couldn't capture a screenshot — your feedback will send without one.
        </Typography>
      )}

      {!capturing && !captureFailed && screenshotUrl && (
        <>
          <Box
            sx={{
              mt: 1,
              borderRadius: 1,
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              // Dim + desaturate when opted out so it's unmistakable
              // the image won't be sent.
              opacity: included ? 1 : 0.4,
              filter: included ? 'none' : 'grayscale(1)',
              transition: 'opacity 150ms, filter 150ms',
            }}
          >
            <Box
              component="img"
              src={screenshotUrl}
              alt="Screenshot of the current page"
              sx={{
                display: 'block',
                width: '100%',
                maxHeight: 200,
                objectFit: 'contain',
                objectPosition: 'top',
                bgcolor: 'action.hover',
              }}
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1 }}
          >
            {included
              ? "Switch this off if the page shows anything you'd rather not share."
              : "This screenshot won't be sent."}
          </Typography>
        </>
      )}
    </Box>
  );
}

// ─── Metadata preview ────────────────────────────────────────────
// Two-column table-ish render. Skips null fields so the panel doesn't
// look noisy on browsers that withhold values. Mobile-specific rows
// only appear when their fields populated, which doubles as a hint of
// which device class the user is on.
function MetadataPreview({ metadata }: { metadata: FeedbackMetadata }) {
  const rows = formatMetadataRows(metadata);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: '4px 12px',
        fontSize: '0.8rem',
        fontFamily: 'monospace',
        color: 'text.secondary',
        wordBreak: 'break-word',
      }}
    >
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ display: 'contents' }}>
          <Box sx={{ whiteSpace: 'nowrap' }}>{label}</Box>
          <Box>{value}</Box>
        </Box>
      ))}
    </Box>
  );
}

function formatMetadataRows(m: FeedbackMetadata): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  const push = (k: string, v: string | number | boolean | null | undefined) => {
    if (v === null || v === undefined || v === '') return;
    rows.push([k, String(v)]);
  };

  push('page', m.page_url);
  push('app version', m.app_version);
  push(
    'viewport',
    m.viewport_width && m.viewport_height ? `${m.viewport_width} × ${m.viewport_height}` : null,
  );
  push(
    'screen',
    m.screen_width && m.screen_height ? `${m.screen_width} × ${m.screen_height}` : null,
  );
  push('pixel ratio', m.device_pixel_ratio);
  push('orientation', m.orientation);
  push('touch', m.is_touch_device);
  push('platform', m.platform);
  push('standalone', m.is_standalone);
  push('connection', m.connection_type);
  push('memory (GB)', m.device_memory);
  push('locale', m.locale);
  push('timezone', m.timezone);
  push('user agent', m.user_agent);
  return rows;
}
